/**
 * ShopCategorySelector — "Vad säljer ni?" chip selector for the Gårdsbutik section.
 * Also renders the shop activation CTA when shop is not enabled.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { track } from '@/lib/analytics';

const SHOP_CATEGORY_OPTIONS = [
  { key: 'gronsaker', label: 'Grönsaker' },
  { key: 'kott', label: 'Kött' },
  { key: 'mejeri', label: 'Mejeri' },
  { key: 'agg', label: 'Ägg' },
  { key: 'honung', label: 'Honung' },
  { key: 'frukt_bar', label: 'Frukt & bär' },
  { key: 'bageri', label: 'Bageri' },
  { key: 'drycker', label: 'Drycker' },
  { key: 'ekologiskt', label: 'Ekologiskt' },
  { key: 'krav', label: 'KRAV-märkt' },
] as const;

interface ShopCategorySelectorProps {
  shopCategories: string[];
  onShopCategoriesChange: (categories: string[]) => void;
  shopEnabled: boolean;
  producerId: string;
  basePath: string;
}

export function ShopCategorySelector({
  shopCategories,
  onShopCategoriesChange,
  shopEnabled,
  producerId,
  basePath,
}: ShopCategorySelectorProps) {
  const navigate = useNavigate();

  const toggleCategory = useCallback((key: string) => {
    const next = shopCategories.includes(key)
      ? shopCategories.filter(k => k !== key)
      : [...shopCategories, key];
    onShopCategoriesChange(next);
  }, [shopCategories, onShopCategoriesChange]);

  const handleShopCta = useCallback(() => {
    track('offerings_next_step_clicked', {
      producer_id: producerId,
      kind: 'shop_enable',
    });
    navigate(`${basePath}/shop`);
  }, [producerId, basePath, navigate]);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-foreground">Vad säljer ni?</h4>
        <p className="text-xs text-muted-foreground mt-0.5">
          Välj kategorier som passar er gårdsbutik.
        </p>
      </div>
      <div className="max-w-[420px] flex flex-wrap gap-2">
        {SHOP_CATEGORY_OPTIONS.map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => toggleCategory(opt.key)}
            className={
              shopCategories.includes(opt.key)
                ? 'px-3 py-1.5 rounded-full text-sm font-medium transition-colors border bg-primary/15 border-primary/30 text-primary'
                : 'px-3 py-1.5 rounded-full text-sm font-medium transition-colors border bg-card border-border text-muted-foreground hover:border-primary/30'
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      {!shopEnabled && (
        <Alert className="border-primary/20 bg-primary/5">
          <AlertTitle className="text-sm font-medium">Nästa steg: aktivera gårdsbutik</AlertTitle>
          <AlertDescription className="text-xs text-muted-foreground mt-1">
            För att kunna visa produkter i Farmly behöver du aktivera gårdsbutiken.
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-7 text-xs gap-1"
              onClick={handleShopCta}
            >
              Gå till Gårdsbutik
              <ArrowRight className="h-3 w-3" />
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
